import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useDashboard } from '../../contexts/DashboardContext';
import TrendModal from '../../components/TrendModal';
import HourlyCallChart from './HourlyCallChart';
import dayjs from 'dayjs';

const CallPerformanceCards = ({ dateRangeState, selectedTeams, selectedAgents, onViewTrendsRef }) => {
  const theme = useTheme();
  const { fetchCallPerformanceTrend } = useDashboard();
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState({});
  
  // Trend modal state
  const [isTrendModalOpen, setIsTrendModalOpen] = useState(false);
  const [selectedTrendData, setSelectedTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const defaultMetrics = [
    {
      id: 'totalCalls',
      title: 'Total Calls',
      value: 0,
      subtitle: ['Fresh: 0, Followups: 0, Not Connected: 0', 'Incoming: 0, Outgoing: 0'],
      icon: <PhoneIcon />,
      color: 'primary',
    },
    {
      id: 'totalCallsTalktime',
      title: 'Total Calls Talktime',
      value: '0h 0m',
      subtitle: 'Incoming: 0m 0s • Outgoing: 0m 0s',
      icon: <AccessTimeIcon />,
      color: 'info',
    },
    {
      id: 'followupCompletion',
      title: 'Followup Completion',
      value: '0%',
      subtitle: 'Scheduled: 0 • Completed: 0',
      icon: <CheckCircleIcon />,
      color: 'primary',
    },
    {
      id: 'siteVisitCompletion',
      title: 'Site Visit Completion',
      value: '0%',
      subtitle: 'Scheduled: 0 • Completed: 0',
      icon: <EventIcon />,
      color: 'success',
    },
  ];

  const [metrics, setMetrics] = useState(defaultMetrics);

  useEffect(() => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
    fetchCallOverview();
  }, [dateRangeState, selectedTeams, selectedAgents]);

  const fetchCallOverview = async () => {
    setLoading(true);
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (selectedTeams && selectedTeams.length > 0) {
        requestData.team_filter = selectedTeams;
      }
      
      // Add agent filter if provided
      if (selectedAgents && selectedAgents.length > 0) {
        requestData.agent_filter = selectedAgents;
      }
      
      const response = await api.post('/report/dashboard/call-performance-discipline/', requestData);
      const data = response.data;
      setOverviewData(data);

      setMetrics([
        {
          id: 'totalCalls',
          title: 'Total Calls',
          value: data.total_calls || 0,
          subtitle: [`Fresh: ${data.fresh_calls || 0}, Followups: ${data.follow_up_calls || 0}, Not Connected: ${data.not_connected_calls || 0}`, `Incoming: ${data.incoming_calls || 0}, Outgoing: ${data.outgoing_calls || 0}`],
          icon: <PhoneIcon />,
          color: 'primary',
          metricType: 'total_calls',
        },
        {
          id: 'totalCallsTalktime',
          title: 'Total Calls Talktime',
          value: data.total_talktime || '0h 0m',
          subtitle: `Incoming: ${data.total_incoming_duration || '0m 0s'} • Outgoing: ${data.total_outgoing_duration || '0m 0s'}`,
          icon: <AccessTimeIcon />,
          color: 'info',
          metricType: 'total_talktime',
        },
        {
          id: 'followupCompletion',
          title: 'Followup Completion',
          value: `${data.followup_completion_percentage || 0}%`,
          subtitle: `Scheduled: ${data.followups_scheduled || 0} • Completed: ${data.followups_completed || 0}`,
          icon: <CheckCircleIcon />,
          color: 'primary',
          metricType: 'followup_completion_percentage',
        },
        {
          id: 'siteVisitCompletion',
          title: 'Site Visit Completion',
          value: `${data.site_visit_completion_percentage || 0}%`,
          subtitle: `Scheduled: ${data.site_visits_scheduled || 0} • Completed: ${data.site_visits_completed || 0}`,
          icon: <EventIcon />,
          color: 'success',
          metricType: 'site_visit_completion_percentage',
        },
      ]);
    } catch (error) {
      console.error('Error fetching call performance data:', error);
      setOverviewData({});
      setMetrics(defaultMetrics);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAllTrends = useCallback(async () => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
    
    const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
    const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
    
    // Check if date range exceeds 60 days
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const daysDiff = end.diff(start, 'day') + 1;
    
    if (daysDiff > 60) {
      alert('Trend data is only available for date ranges up to 60 days. Please select a shorter date range.');
      return;
    }
    
    setTrendLoading(true);
    setIsTrendModalOpen(true);
    setSelectedTrendData({
      title: 'Agent Performance & Discipline - Daily Trends',
      data: [],
      metrics: []
    });
    
    try {
      // Fetch all metrics in a single API call
      const allMetricTypes = metrics.filter(m => m.metricType).map(m => m.metricType);
      const response = await fetchCallPerformanceTrend(
        startDate,
        endDate,
        null, // metricType - not used when metric_types is provided
        selectedTeams,
        selectedAgents,
        allMetricTypes // Pass all metric types
      );
      
      if (response && response.daily_data && response.metric_types) {
        // Format dates for display
        const formattedData = response.daily_data.map(item => ({
          date: dayjs(item.date).format('MMM DD'),
          ...response.metric_types.reduce((acc, metricType) => {
            acc[metricType] = item[metricType] || 0;
            return acc;
          }, {})
        }));
        
        // Map metric types to their metadata
        const trendMetrics = response.metric_types.map(metricType => {
          const metric = metrics.find(m => m.metricType === metricType);
          return {
            key: metricType,
            title: metric?.title || metricType,
            color: metric ? theme.palette[metric.color].main : '#000000'
          };
        });
        
        setSelectedTrendData({
          title: 'Agent Performance & Discipline - Daily Trends',
          data: formattedData,
          metrics: trendMetrics
        });
      }
    } catch (error) {
      console.error('Error fetching trend data:', error);
    } finally {
      setTrendLoading(false);
    }
  }, [dateRangeState, selectedTeams, selectedAgents, metrics, fetchCallPerformanceTrend, theme]);

  const handleCloseTrendModal = () => {
    setIsTrendModalOpen(false);
    setSelectedTrendData(null);
  };

  // Expose handleViewAllTrends via ref
  useEffect(() => {
    if (onViewTrendsRef) {
      onViewTrendsRef.current = handleViewAllTrends;
    }
  }, [onViewTrendsRef, handleViewAllTrends]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Hourly Call Chart */}
      <Box sx={{ mb: 3 }}>
        <HourlyCallChart data={overviewData?.hourly_data} loading={loading} />
      </Box>

      <Grid container spacing={3}>
        {metrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.id}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette[metric.color].main, 0.2)}`,
                background: 'background.paper',
                boxShadow: `0 2px 8px ${alpha(theme.palette[metric.color].main, 0.08)}`,
                transition: 'all 0.3s ease',
                height: '100%',
                '&:hover': {
                  boxShadow: `0 4px 12px ${alpha(theme.palette[metric.color].main, 0.2)}`,
                  transform: 'translateY(-2px)',
                  border: `1px solid ${alpha(theme.palette[metric.color].main, 0.4)}`,
                },
              }}
            >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  {metric.title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette[metric.color].main,
                    mb: 0.5,
                    fontSize: '1.75rem',
                  }}
                >
                  {metric.value}
                </Typography>
                {metric.subtitle && (
                  <Box>
                    {Array.isArray(metric.subtitle) ? (
                      metric.subtitle.map((line, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                          {line}
                        </Typography>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        {metric.subtitle}
                      </Typography>
                    )}
                  </Box>
                )}
                {metric.chip && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Chip
                      label={metric.chip}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                    {metric.chipDetail && (
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        {metric.chipDetail}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette[metric.color].main, 0.1),
                  color: theme.palette[metric.color].main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {metric.icon}
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
      </Grid>
      
      {/* Trend Modal */}
      <TrendModal
        open={isTrendModalOpen}
        onClose={handleCloseTrendModal}
        title={selectedTrendData?.title}
        data={trendLoading ? [] : selectedTrendData?.data}
        metrics={selectedTrendData?.metrics}
        chartType="line"
      />
      
      {/* Loading overlay for trend modal */}
      {isTrendModalOpen && trendLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default CallPerformanceCards;

